/**
 * C2 — which car, before the wizard opens.
 *
 * A separate destination rather than a seventh step, because it answers a different question: the
 * wizard is «this order, in detail», and by the time it opens a car is already chosen. Keeping it
 * out also means «добавить ещё машину» from the fuel step lands here instead of needing a second,
 * near-identical picker inside the sheet.
 *
 * **Three modes, one list.** `start` begins a new order; `add` appends to the выезд being built;
 * `swap` replaces the primary car mid-flow. All three write straight to the stored draft rather
 * than through React state, because the wizard is a different route and remounts on arrival — the
 * draft in storage *is* the shared state, and it already survives a reload.
 *
 * **`add` shows only cars on the same fuel.** One выезд carries one fuel and the backend refuses a
 * mixed one with a 400; a row that ends in that is worse than a row that explains itself. `swap`
 * deliberately does not filter — changing the whole выезд's fuel is a legitimate thing to do, and
 * `withCar` drops the extras that no longer fit.
 *
 * **Cars with a running order are greyed out, using `car_ids` from `/orders/active`.** One active
 * order per car is enforced by a unique index, so picking such a car ends in a 409 at submit. That
 * field was added for exactly this: brand and model cannot answer it, because two identical cars
 * in one garage are indistinguishable by them and matching on those greys out the wrong one.
 *
 * A backend that predates the field omits it, and then nothing is marked — offering a car the
 * server refuses is bad, but marking the wrong one as taken is worse.
 */
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  fetchCars,
  fetchFuelTypes,
  isFuelAccepted,
  type ConsumerCar,
  vehicleLine,
} from '@/entities/car/api';
import { fetchActiveOrders } from '@/entities/order/api';
import { loadOrderDraft, saveOrderDraft } from '@/entities/order/draftStorage';
import { canJoinTrip, withCar, withExtraCar } from '@/entities/order/model/carEdits';
import {
  canAddAnotherCar,
  createOrderDraft,
  maxCarsPerOrderOrDefault,
} from '@/entities/order/model/draft';
import { friendlyErrorMessage } from '@/shared/api/errors';
import { Banner } from '@/shared/ui/Banner';
import { Button } from '@/shared/ui/Button';
import { CarBrandLogo } from '@/shared/ui/CarBrandLogo';
import { Content, Screen } from '@/shared/ui/Layout';
import { EmptyState } from '@/shared/ui/RowList';
import { IconButton, TopBar } from '@/shared/ui/TopBar';
import { BackIcon, CarIcon } from '@/shared/ui/icons';
import { useDocumentTitle } from '@/shared/lib/useDocumentTitle';

/** «Solaris · А123АВ77» — one line, and the same one the summary reads back later. */
export function carLine(car: ConsumerCar): string {
  // Через `vehicleLine`: у катера номера нет, и «Yamaha 242X · » с пустым хвостом — не строка.
  return vehicleLine(car);
}

export function CarPickerScreen() {
  const navigate = useNavigate();
  // `strict: false` like the other screens under the protected layout: the route's own id is the
  // nested `/protected/order/car`, and naming it here couples the screen to the tree's shape.
  const search: { add?: boolean; swap?: boolean } = useSearch({ strict: false });
  const draft = loadOrderDraft();

  const mode: 'start' | 'add' | 'swap' =
    search.add === true ? 'add' : search.swap === true && draft !== null ? 'swap' : 'start';

  const TITLE = { start: 'Что заправляем', add: 'Добавить ещё', swap: 'Сменить технику' };
  useDocumentTitle(TITLE[mode]);

  const cars = useQuery({ queryKey: ['cars'], queryFn: fetchCars });
  const active = useQuery({ queryKey: ['orders', 'active'], queryFn: fetchActiveOrders });
  // Адрес в черновике есть не всегда: с главного экрана машину выбирают ДО него. Есть — спрашиваем
  // справочник по городу (там свои стопы), нет — общий ответ, а городской стоп поймает шаг объёма.
  const at =
    draft === null || draft.latitude === null || draft.longitude === null
      ? null
      : { lat: draft.latitude, lon: draft.longitude };
  const fuelTypes = useQuery({
    queryKey: ['fuel-types', at?.lat ?? null, at?.lon ?? null],
    queryFn: () => fetchFuelTypes(at),
  });

  const busyCarIds = new Set((active.data ?? []).flatMap((order) => order.car_ids ?? []));

  const alreadyInTrip = new Set<string>(
    draft === null
      ? []
      : [draft.carId, ...draft.extraCars.map((c) => c.carId)].filter(
          (id): id is string => id !== null,
        ),
  );

  function choose(car: ConsumerCar) {
    if (mode === 'add') {
      const current = draft ?? createOrderDraft();
      const next = withExtraCar(current, {
        carId: car.id,
        label: carLine(car),
        liters: '',
        fuelTypeCode: car.fuel_type_code,
        tankVolumeLiters: car.tank_volume_liters,
        brand: car.brand,
      });
      // `withExtraCar` refuses a duplicate or a car past the cap by returning the draft unchanged
      // — the same guard the rows already apply, kept here because a row is not a control.
      if (next === current) return;
      saveOrderDraft({ ...next, step: 'fuel' });
    } else if (mode === 'swap' && draft !== null) {
      // A swap, not a restart: address, presence, time and card survive. `withCar` decides what
      // does not — see its comment for the fuel rule and the extras it drops.
      saveOrderDraft(
        withCar(draft, {
          id: car.id,
          label: carLine(car),
          brand: car.brand,
          fuelTypeCode: car.fuel_type_code,
          tankVolumeLiters: car.tank_volume_liters,
        }),
      );
    } else {
      // A fresh draft, not a patched one: the previous order's address, comment and card belong to
      // the order that was abandoned, and quietly reusing them is how somebody sends a driver to
      // last week's address.
      saveOrderDraft(
        createOrderDraft({
          carId: car.id,
          carLabel: carLine(car),
          carBrand: car.brand,
          fuelTypeCode: car.fuel_type_code,
          tankVolumeLiters: car.tank_volume_liters,
        }),
      );
    }
    void navigate({ to: '/order/new' });
  }

  const all = cars.data ?? [];
  /* Какое топливо мы сегодня наливаем. Справочник, а не догадка: тумблером в админке вид топлива
     закрывается на время («бензовоз в ремонте»), и заказ по машине на нём не примут. Показать это
     ЗДЕСЬ — единственный способ не довести человека до отказа на экране оплаты. */
  const stoppedFuels = new Map(
    (fuelTypes.data ?? [])
      .filter((fuel) => !isFuelAccepted(fuel))
      .map((fuel) => [fuel.code, fuel.unavailable_note ?? `${fuel.name} сейчас не заправляем`]),
  );
  // Filtered, not greyed: a diesel van in a petrol выезд is not a row somebody should have to read
  // and dismiss — it simply is not one of the options.
  const list =
    mode === 'add' && draft !== null
      ? all.filter((car) => canJoinTrip(draft, car.fuel_type_code))
      : all;
  const hiddenByFuel = all.length - list.length;
  // Имя топлива выезда — со строки той машины, которая его и задала. В черновике лежит только код.
  const fuelName =
    draft === null
      ? null
      : (all.find((car) => car.fuel_type_code === draft.fuelTypeCode)?.fuel_type_name ?? null);
  const full = mode === 'add' && draft !== null && !canAddAnotherCar(draft);

  /**
   * Adds a car without losing the order.
   *
   * The form is told to come back here, in the same mode — see `returnTo` on `/cars/new`. Landing
   * in the garage instead would leave somebody to find their own way back to a half-filled заявка.
   */
  function addCar() {
    const returnTo = mode === 'add' ? 'picker-add' : mode === 'swap' ? 'picker-swap' : 'picker';
    void navigate({ to: '/cars/new', search: { returnTo } });
  }

  return (
    <Screen>
      <TopBar
        title={TITLE[mode]}
        leading={
          <IconButton
            label="Назад"
            onClick={() => void navigate({ to: mode === 'start' ? '/' : '/order/new' })}
          >
            <BackIcon />
          </IconButton>
        }
      />

      <Content centered={list.length === 0}>
        {cars.isPending ? (
          <p role="status" aria-live="polite" className="py-10 text-center text-[13px] text-ink-faint">
            Загружаем гараж…
          </p>
        ) : cars.isError ? (
          <Banner tone="warning" title="Не удалось загрузить технику">
            {friendlyErrorMessage(cars.error)}
            <div className="mt-2">
              <Button variant="secondary" onClick={() => void cars.refetch()}>
                Повторить
              </Button>
            </div>
          </Banner>
        ) : list.length === 0 ? (
          <EmptyState
            icon={<CarIcon size={34} />}
            title="В парке пусто"
            description="Добавьте технику, чтобы оформить заказ"
            action={<Button onClick={addCar}>Добавить технику</Button>}
          />
        ) : (
          <div className="flex flex-col gap-3">
            {full && draft !== null && (
              <Banner tone="info">
                В один выезд помещается не больше {maxCarsPerOrderOrDefault(draft)} единиц техники.
              </Banner>
            )}

            {/* Правило называется вслух ВСЕГДА, а не только когда список поредел: человек, у
                которого в гараже одна машина, иначе узнаёт про него впервые от сервера — отказом
                на оплате. А когда техника действительно скрыта, добавляется и это. */}
            {mode === 'add' && (
              <Banner tone="info">
                Один выезд — одно топливо
                {fuelName === null ? '' : ` (${fuelName})`}.
                {hiddenByFuel > 0 ? ' Техника на другом топливе в списке не показана.' : ''}
              </Banner>
            )}

            {/* Замена основной машины меняет топливо всего выезда — и уводит из него тех, кто на
                другом. Сказать это до нажатия честнее, чем показать поредевший список после. */}
            {mode === 'swap' && draft !== null && draft.extraCars.length > 0 && (
              <Banner tone="info">
                Один выезд — одно топливо. Если сменить его, техника на прежнем выйдет из заказа.
              </Banner>
            )}

            <ul className="flex flex-col gap-2">
              {list.map((car) => {
                const chosen = alreadyInTrip.has(car.id);
                const busy = busyCarIds.has(car.id);
                const fuelStopped = stoppedFuels.get(car.fuel_type_code);
                const blocked =
                  busy || fuelStopped !== undefined || (mode === 'add' && (chosen || full));

                return (
                  <li key={car.id}>
                    <button
                      type="button"
                      disabled={blocked}
                      onClick={() => choose(car)}
                      className={`flex w-full items-center gap-3 rounded-md border border-line px-3.5 py-3 text-left ${
                        blocked ? 'opacity-55' : ''
                      }`}
                    >
                      <CarBrandLogo brand={car.brand} size={32} />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[14px] font-bold leading-[1.3]">
                          {carLine(car)}
                        </span>
                        <span className="block text-[12.5px] leading-[1.28] text-ink-soft">
                          {busy
                            ? 'Уже есть активная заявка'
                            : chosen
                              ? 'Уже в этом заказе'
                              : /* Причина вместо названия топлива: человек и так знает, что у него
                                   дизель, — ему нужно знать, почему нельзя заказать. */
                                (fuelStopped ?? car.fuel_type_name)}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>

            {/* Offered next to the list, not only when the garage is empty. «Нужной машины нет»
                is exactly as likely with three cars as with none, and until now the only way out
                was to leave the order, find the garage, add a car and walk back in. */}
            <Button variant="secondary" onClick={addCar}>
              Добавить технику
            </Button>
          </div>
        )}
      </Content>
    </Screen>
  );
}
